import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { customAlphabet } from 'nanoid';

import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useDataHelper } from 'utils/dataHelper';
import { delayForCreation, allowedModeratorEmail } from 'utils/constants';
import {
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
  TimePicker,
} from '@material-ui/pickers';

import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import DateFnsUtils from '@date-io/date-fns';

import {
  format,
  addDays,
  isAfter,
  isEqual,
  formatISO,
  setSeconds,
  isBefore,
  differenceInMinutes,
} from 'date-fns';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import { theme } from 'theme';

import { useTheme, styled, withStyles } from '@material-ui/core/styles';
import { Grid, TextField, useMediaQuery, Tooltip } from '@material-ui/core';

import AcceptInvitationDialog from './../userArea/AcceptInvitationDialog';

import EmailIcon from '@material-ui/icons/Email';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import {
  SdmTypography,
  SdmSpacer,
  SdmTextButton,
  SdmSelect,
  SdmRichText,
  SdmImageUploader,
  richTextToHTML,
} from 'components';

import InvitationList from './BreakoutRoomForm/InvitationList';
import DeleteDialog from './BreakoutRoomForm/DeleteDialog';
import NoteDialog from './BreakoutRoomForm/NoteDialog';

import { AuthContext } from 'context/AuthContextProvider';
import { LoaderContext } from 'context/LoaderContextProvider';

import { breakoutRoomModel } from 'entities/breakoutRoomModel';
import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { breakoutRoomSpeakerModel } from 'entities/breakoutRoomSpeakerModel';

import { useNetwork } from 'services/network';
import aws from 'services/aws';
import {
  cdnUrl as CDN_URL,
  launchDate as LAUNCH_DATE,
  ENV,
} from 'utils/constants';
import SpeakerBox from './BreakoutRoomForm/SpeakerBox';

const InviteUsersLink = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  height: '100%',
});

const DeleteLink = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const calendarColor = {
  main: theme.colors.primary.red,
};

const defaultMaterialTheme = createMuiTheme({
  palette: {
    primary: calendarColor,
  },
  typography: {
    fontFamily: 'Suisse-Intl',
  },
});

export const ButtonsContainer = styled('div')(({ isformdisabled }) => ({
  display: 'flex',
  justifyContent: isformdisabled ? 'flex-end' : 'space-between',
  alignItems: 'center',
  height: '100%',
}));

export const CustomFormTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.colors.primary.black}`,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Suisse-Intl',
      fontSize: 26,
    },
    '& .MuiFormLabel-root': {
      fontFamily: 'Suisse-Intl',
      color: `${theme.colors.primary.black}`,
    },
  },
})(TextField);

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const nanoid = customAlphabet(alphabet, 12);

const BreakoutRoomForm = () => {
  // Hooks
  const { user } = useContext(AuthContext);
  const { setLoaderActive } = useContext(LoaderContext);
  const intl = useIntl();
  const i18n = translations.breakoutRoomForm(intl);
  const dataHelper = useDataHelper();
  const network = useNetwork();
  const history = useHistory();

  const canCreateLiveChat = allowedModeratorEmail === user.email;

  const defaultValue = {
    topic: undefined,
    start: undefined,
    end: undefined,
    date: undefined,
    sessionType: !canCreateLiveChat ? 'breakout_session' : undefined,
    description: undefined,
    coverImage: undefined,
    speakers: [],
  };

  const {
    reset,
    handleSubmit,
    getValues,

    errors,
    control,
  } = useForm({
    defaultValues: defaultValue,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'speakers',
  });

  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  const mediaTheme = useTheme();
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));

  //States
  const [currentBreakoutRoom, setCurrentBreakoutRoom] = useState();
  const [unparsedBreakoutRoom, setUnparsedBreakoutRoom] = useState();
  const [selectedBrType, setSelectedBrType] = useState(
    !canCreateLiveChat
      ? {
          label: 'Sessione Breakout Room',
          value: 'breakout_session',
        }
      : null
  );
  const [invitations, setInvitations] = useState();
  const [speakers, setSpeakers] = useState();
  const [speakersToRemove, setSpeakersToRemove] = useState([]);
  const [customError, setCustomError] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState([]);
  const [noteDialog, setNoteDialog] = useState({ isOpen: false });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false });
  const [bookedDialog, setBookedDialog] = useState({ isOpen: false, text: '' });

  //Refs
  const sessionSelectRef = useRef(null);

  const getBrOptions = () => {
    return Object.values(breakoutRoomModel.enums.types).map((s) => ({
      label: i18n.fields.sessionType.values[s],
      value: s,
    }));
  };

  const brOptionTypes = getBrOptions();

  //Effects
  useEffect(() => {
    const historyState = history.location.state;
    if (historyState?.breakoutRoom?.id) {
      const br = historyState.breakoutRoom;
      fetchInvitations(br.id);
    } else {
      setInvitations([]);
    }
    fetchAllBr();
  }, []);

  useEffect(() => {
    if (invitations) {
      fetchFromHistoryState();
    }
  }, [invitations]);

  const fetchAllBr = async () => {
    const otherBr = await breakoutRoomModel.gql.listAdminBreakoutRooms();
    const filtered = otherBr?.items
      ?.map((br) => ({ start: br.start, end: br.end, date: br.date }))
      .filter((br) => isAfter(new Date(br.date), new Date()));

    setAlreadyBooked(filtered);
  };

  const fetchFromHistoryState = () => {
    const historyState = history.location.state;
    if (historyState?.breakoutRoom?.id) {
      const br = historyState.breakoutRoom;
      const isChatType = br.type === breakoutRoomModel.enums.types.CHAT;
      const start = isChatType
        ? formatISO(new Date(br.start)).slice(0, 19)
        : new Date(br.start);
      const end = isChatType
        ? formatISO(new Date(br.end)).slice(0, 19)
        : new Date(br.end);
      const parsedBreakoutRoom = {
        id: br.id,
        topic: br.name,
        date: new Date(br.date),
        start: start,
        end: end,
        sessionType: brOptionTypes.find((s) => s.value === br.type),
        type: br.type,
        invitations: br.invitations?.items || [],
        description: br.description,
        coverImage: br.coverImage,
        speakers:
          br?.speakers?.items.map((sp) => ({
            ...sp,
            oldId: sp?.id || sp?.oldId,
          })) || [],
      };
      setUnparsedBreakoutRoom(br);
      setCurrentBreakoutRoom(parsedBreakoutRoom);
      setSpeakers(br?.speakers?.items || []);
      setSelectedBrType(parsedBreakoutRoom.sessionType);
    }
  };

  useEffect(() => {
    if (currentBreakoutRoom) {
      reset(currentBreakoutRoom);
    }
  }, [currentBreakoutRoom]);

  //Functions
  const fetchInvitations = async (breakoutRoomId) => {
    showLoader();
    const res = await breakoutRoomInviteModel.gql.listBreakoutRoomInvitations(
      breakoutRoomId
    );
    setInvitations(res?.invitations?.items || []);
    setSpeakers(res?.speakers?.items || []);
    hideLoader();
  };

  const checkUpdateIsDisabled = () => {
    if (invitations) {
      //se contiene inviti canceled la BR può essere aggiornata senza necessariamente avvisare l'utente
      //altrimenti devo prima svuotare la BR dagli inviti
      const invited = invitations.filter(
        (invite) =>
          invite.state !== breakoutRoomInviteModel.enums.status.CANCELED
      );
      return invited.length > 0;
    }
    //non ci sono inviti, posso aggiornare / creare la BR
    return false;
  };

  const isFormDisabled = () => {
    return dataHelper.checkPassedDate(unparsedBreakoutRoom?.end);
  };

  const isStarted = () => {
    return dataHelper.checkPassedDate(unparsedBreakoutRoom?.start);
  };

  const handleInvite = () => {
    const historyState = history.location.state;
    history.push('/invite', historyState);
  };

  const handleDeleteBr = async () => {
    const deleleteList = [];
    if (invitations && !isFormDisabled()) {
      invitations.map((i) => {
        if (
          i.status !== breakoutRoomInviteModel.enums.status.CANCELED &&
          i.status !== breakoutRoomInviteModel.enums.status.REJECTED
        )
          deleleteList.push(i);
      });
    }
    history.push('/deleteBreakoutRoom', {
      invitationList: deleleteList,
      breakoutRoom: unparsedBreakoutRoom,
    });
  };

  const handleBan = async (data) => {
    history.push('/removeUser', {
      invitationList: [data],
      breakoutRoom: unparsedBreakoutRoom,
    });
  };

  const handleShowNote = async (data) => {
    setNoteDialog({
      isOpen: true,
      data: data,
    });
  };

  const handleDelete = async (data) => {
    setDeleteDialog({
      isOpen: true,
      data: data,
    });
  };

  const handlePromote = async (invitation, promotionType) => {
    const input = {
      id: invitation.id,
      invitationType: promotionType,
    };
    const res = await breakoutRoomInviteModel.gql.update(input);
    return res;
  };

  const confirmDelete = async (data) => {
    showLoader();
    await breakoutRoomInviteModel.gql.remove({ id: data.id });
    fetchInvitations(currentBreakoutRoom.id);
    //non serve chiudere illoader, ci pensa il fetch successivamente
  };

  const handleRoleCreation = async (inv) => {
    showLoader();
    const input = {
      id: inv.id,
      jobTitle: inv.jobTitle,
    };
    await breakoutRoomInviteModel.gql.update(input);
    fetchInvitations(currentBreakoutRoom.id);
  };

  const handleBack = () => {
    history.push('/', null);
  };

  const getMinDate = () => {
    const delayedDate = addDays(setSeconds(new Date(), 0), delayForCreation);

    const launchDate = new Date(LAUNCH_DATE);

    return currentBreakoutRoom?.date
      ? new Date(currentBreakoutRoom.date)
      : isAfter(delayedDate, launchDate)
      ? delayedDate
      : launchDate;
  };

  const isBetweenNineAmSixPm = (hourDate) => {
    if (hourDate) {
      const nineAm = new Date(hourDate).setHours(9, 0, 0, 0);
      const sixPm = new Date(hourDate).setHours(18, 0, 0, 0);
      return (
        new Date(hourDate) >= new Date(nineAm) &&
        new Date(hourDate) <= new Date(sixPm)
      );
    }
  };

  const maxOneHour = (startDate, endDate) => {
    return differenceInMinutes(endDate, startDate) <= 60;
  };

  const disableDates = (date) => {
    /* date interdites for all year ! 
    01/01
    06/01
    25/04
    01/05
    02/06
    15/08
    01/11
    25/12 
    26/12
   
    */
    // in the following array format month are starting from 0 till 11
    const dateInterditesRaw = [
      new Date(date.getFullYear(), 0, 1),
      new Date(date.getFullYear(), 0, 6),
      new Date(date.getFullYear(), 3, 25),
      new Date(date.getFullYear(), 4, 1),
      new Date(date.getFullYear(), 5, 2),
      new Date(date.getFullYear(), 7, 15),
      new Date(date.getFullYear(), 10, 1),
      new Date(date.getFullYear(), 11, 25),
      new Date(date.getFullYear(), 11, 26),
    ];

    /* make a new array with the getTime() without it date comparaison are 
    never true  */

    const dateInterdites = dateInterditesRaw.map((arrVal) => arrVal.getTime());

    /*exclude all sunday and use the includes array method to check if the 
    date.getTime() value is 
    in the array dateInterdites */

    return (
      date.getDay() === 0 ||
      date.getDay() === 6 ||
      dateInterdites.includes(date.getTime())
    );
  };

  const isCheckedAvailability = (start, end) => {
    const countFounded = alreadyBooked.filter(
      (br) =>
        (isBefore(start, new Date(br.start)) &&
          isAfter(end, new Date(br.end))) ||
        (isAfter(end, new Date(br.start)) && isBefore(end, new Date(br.end))) ||
        (isAfter(start, new Date(br.start)) &&
          isBefore(start, new Date(br.end))) ||
        (isEqual(start, new Date(br.start)) && isEqual(end, new Date(br.end)))
    ).length;

    if (countFounded >= 2) {
      return false;
    } else {
      return true;
    }
  };

  const copyPageUrl = async () => {
    if (location.search) {
      const link = dataHelper.joinChat(currentBreakoutRoom.id, false, true);
      try {
        showLoader();
        await navigator.clipboard.writeText(link);
        hideLoader();
        setLinkCopied(true);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  const parseBr = (data) => {
    const type = !canCreateLiveChat
      ? 'breakout_session'
      : data.sessionType.value;
    const isChatType = type === breakoutRoomModel.enums.types.CHAT;
    const day = !isChatType ? new Date(data.date) : new Date(data.start);
    const start = new Date(!isChatType ? data.date : data.start);
    const end = new Date(!isChatType ? data.date : data.end);
    if (!isChatType) {
      const startHours = format(data.start, 'HH:mm');
      const endHours = format(data.end, 'HH:mm');
      const _start = startHours.split(':');
      const _end = endHours.split(':');
      start.setHours(parseInt(_start[0]), parseInt(_start[1]), 0, 0);
      end.setHours(parseInt(_end[0]), parseInt(_end[1]), 0, 0);
    }
    const descMobile = richTextToHTML(data.description);
    const input = {
      breakoutRoomBrandId: user.id,
      name: data.topic,
      date: day,
      start: start,
      end: end,
      type: type,
      ...(!!data.description &&
        (typeof data.description === 'string'
          ? data.description
          : JSON.stringify(data.description)) !==
          currentBreakoutRoom?.description && {
          description: JSON.stringify(data.description),
          descriptionMobile: descMobile,
        }),
    };
    if (
      isChatType ||
      (isBetweenNineAmSixPm(start) &&
        isBetweenNineAmSixPm(end) &&
        maxOneHour(start, end) &&
        isBefore(start, end))
    ) {
      if (isChatType || isCheckedAvailability(start, end)) {
        return input;
      } else {
        setBookedDialog({ isOpen: true, text: i18n.bookedDialog });
      }
    } else {
      setCustomError(true);
      return;
    }
  };

  const onSubmit = async (data) => {
    showLoader();
    const input = !customError ? parseBr(data) : null;
    if (input) {
      let isUpdate = true;
      const speakerPromises = [];
      try {
        let newBr = {};
        if (currentBreakoutRoom?.id) {
          newBr = { ...input, id: currentBreakoutRoom.id };
          await breakoutRoomModel.gql.update(newBr);
          if (speakersToRemove.length) {
            speakersToRemove.map((sId) => {
              const foundSpToRemove = speakers.find((sp) => sp.id === sId);
              if (foundSpToRemove) {
                speakerPromises.push(
                  aws.API.deleteBucketObject({
                    key: foundSpToRemove?.coverImage,
                  })
                );
                speakerPromises.push(
                  breakoutRoomSpeakerModel.gql.remove({
                    id: foundSpToRemove.id,
                  })
                );
              }
            });
          }
        } else {
          const brResult = await breakoutRoomModel.gql.create(input);
          isUpdate = false;
          newBr = {
            ...input,
            id: brResult.id,
          };
        }
        //Handle speakers logic, create speaker, create image,

        const parsedSpeakers = [];
        if (data?.speakers?.length) {
          const _speakers = data.speakers;
          await Promise.all(
            _speakers.map(async (s) => {
              const uploadedImg = s?.coverImage;

              let imagePath = '';
              if (uploadedImg && typeof uploadedImg !== 'string') {
                if (speakers && speakers.length) {
                  const speakerChooseImg = speakers.find(
                    (sp) =>
                      sp.givenName === s.giveName &&
                      sp.familyName === s.familyName
                  )?.coverImage;
                  if (speakerChooseImg)
                    await aws.API.deleteBucketObject({
                      key: speakerChooseImg,
                    });
                }
                const splitted = uploadedImg.info.name.split('.');
                const ext = splitted[splitted.length - 1].toLowerCase();
                const fileName = splitted[0].toLowerCase();
                imagePath = `${ENV}/brand/${user?.externalId}/breakoutroom/${
                  newBr.id
                }/speakers/${s.givenName}${
                  s.familyName
                }/${fileName}-${nanoid()}.${ext}`;
                const presignedUrl = await aws.API.getPresignedFromLambda({
                  key: imagePath,
                });
                const base64Data = new Buffer.from(
                  uploadedImg.data.replace(/^data:image\/\w+;base64,/, ''),
                  'base64'
                );
                if (presignedUrl) {
                  const putResponse = await network.sendImage(
                    presignedUrl,
                    base64Data,
                    {
                      headers: {
                        'Content-type': uploadedImg.info.type,
                      },
                    }
                  );
                  if (putResponse?.status !== 200) {
                    imagePath = '';
                  }
                }
              } else {
                imagePath = uploadedImg;
              }
              s.coverImage = imagePath;
              const speakerInput = {
                givenName: s?.givenName || '',
                familyName: s?.familyName || '',
                coverImage: s?.coverImage || '',
                jobTitle: s?.jobTitle || '',
                breakoutRoomSpeakerBreakoutRoomId: newBr.id,
              };

              if (
                s?.oldId &&
                !speakersToRemove.find((spId) => spId === s.oldId)
              ) {
                await breakoutRoomSpeakerModel.gql.update({
                  ...speakerInput,
                  id: s.oldId,
                });
                parsedSpeakers.push(s);
              } else if (!s?.oldId) {
                const createdSpeakerId =
                  await breakoutRoomSpeakerModel.gql.create(speakerInput);
                parsedSpeakers.push({ ...s, oldId: createdSpeakerId.id });
              }
            })
          );
        }
        if (speakerPromises.length) {
          await Promise.all(speakerPromises);
        }

        newBr.speakers = {
          items: parsedSpeakers,
        };

        const uploadedFile = data.coverImage;

        if (uploadedFile && uploadedFile !== currentBreakoutRoom?.coverImage) {
          if (isUpdate && currentBreakoutRoom?.coverImage) {
            //Siamo in Update: delete-->Creation
            await aws.API.deleteBucketObject({
              key: currentBreakoutRoom.coverImage,
            });
            //console.log('DELETE RESPONSE:: ', deleteResponse);
          }
          //Siamo in Creation: presinged-->putObject
          const splitted = uploadedFile.info.name.split('.');
          const ext = splitted[splitted.length - 1].toLowerCase();
          const fileName = splitted[0].toLowerCase();
          const objectKey = `${ENV}/brand/${user?.externalId}/breakoutroom/${
            newBr.id
          }/coverImage/${fileName}-${nanoid()}.${ext}`;
          const presignedUrl = await aws.API.getPresignedFromLambda({
            key: objectKey,
          });
          const base64Data = new Buffer.from(
            uploadedFile.data.replace(/^data:image\/\w+;base64,/, ''),
            'base64'
          );

          if (presignedUrl) {
            const putResponse = await network.sendImage(
              presignedUrl,
              base64Data,
              {
                headers: {
                  'Content-type': uploadedFile.info.type,
                },
              }
            );
            //console.log('PUT RESPONSE:: ', putResponse);
            if (putResponse?.status === 200) {
              const updateInput = {
                id: newBr.id,
                coverImage: objectKey,
              };
              breakoutRoomModel.gql.update(updateInput);
            }
            newBr.coverImage = objectKey;
          }
        }

        const historyState = history.location.state;
        const state = {
          ...historyState,
          breakoutRoom: {
            ...unparsedBreakoutRoom,
            ...newBr,
          },
        };
        history.replace(history.location.pathname, state);
        fetchFromHistoryState();

        hideLoader();
      } catch (e) {
        console.error('ERROR:: ', e);
      }
    } else {
      hideLoader();
    }
  };

  const handleRemove = (idx, speakerId) => {
    remove(idx);
    if (speakerId) {
      const _speakers = [...speakersToRemove, speakerId];
      setSpeakersToRemove(_speakers);
    }
  };

  /*
   * RETURN CONTROL
   */
  if (!invitations) return null;

  /*
   * RENDERS
   */

  const renderDeleteDialog = () => {
    if (deleteDialog.isOpen)
      return (
        <DeleteDialog
          onAgree={(data) => confirmDelete(data)}
          onClose={() => {
            setDeleteDialog({ isOpend: false });
          }}
          data={deleteDialog.data}
          i18n={i18n.deleteDialog}
        />
      );
    return null;
  };

  const renderNoteDialog = () => {
    if (noteDialog.isOpen)
      return (
        <NoteDialog
          onClose={() => {
            setNoteDialog({ isOpen: false });
          }}
          data={noteDialog.data}
          i18n={i18n.noteDialog}
        />
      );
    return null;
  };

  const renderError = (key, labelKey) => {
    if (errors[key] && errors[key]?.type === 'required')
      return (
        <SdmTypography
          style={{ color: `${theme.colors.primary.red}` }}
          variant={'textBold'}
        >
          {i18n.fields[labelKey ? labelKey : key].error}
        </SdmTypography>
      );
    return null;
  };

  const renderBookedDialog = () => {
    if (!bookedDialog.isOpen) return null;
    return (
      <AcceptInvitationDialog
        onClose={() => {
          setBookedDialog({ isOpen: false, text: '' });
        }}
        i18n={bookedDialog.text}
      />
    );
  };

  const renderForm = () => {
    const isChatType =
      selectedBrType?.value === breakoutRoomModel.enums.types.CHAT;
    return (
      <Grid
        item
        xs={12}
        style={{
          padding: '0 20px',
        }}
      >
        <SdmTypography
          textAlign={'center'}
          variant={isXsDown ? 'h1' : 'pageTitle'}
          style={{ marginTop: 50 }}
        >
          {i18n.title}
        </SdmTypography>
        <div style={{ margin: '0px auto ', maxWidth: 736 }}>
          {renderBookedDialog()}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid item xs={12}>
              <Grid container>
                <SdmSpacer height={24} />
                <Grid item xs={7}>
                  <SdmTypography variant={isXsDown ? 'h2' : 'h1'}>
                    {i18n.subtitle}
                  </SdmTypography>
                </Grid>
                <Grid item xs={5}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isSmDown ? 'column' : 'row',
                      alignItems: isSmDown ? 'flex-end' : 'center',
                      flexWrap: 'flex-wrap',
                      height: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {currentBreakoutRoom && isChatType && (
                      <DeleteLink style={{ marginRight: isSmDown ? 0 : 5 }}>
                        {linkCopied ? (
                          <CheckIcon
                            style={{
                              // height: 15,
                              // width: 15,
                              border: `2px solid ${theme.colors.primary.red}`,
                              marginRight: 8,
                              borderRadius: '100%',
                              color: theme.colors.primary.red,
                            }}
                          />
                        ) : (
                          <FileCopyIcon
                            style={{
                              marginRight: 8,
                              borderRadius: '100%',
                              color: theme.colors.primary.red,
                            }}
                          />
                        )}
                        <SdmTypography
                          variant={'h4'}
                          margin={0}
                          style={{
                            color: theme.colors.primary.red,
                            cursor: 'pointer',
                            marginRight: isSmDown ? 0 : 10,
                          }}
                          onClick={copyPageUrl}
                        >
                          {linkCopied
                            ? i18n.copiedToClipboard
                            : i18n.copyToClipboard}
                        </SdmTypography>
                      </DeleteLink>
                    )}
                    {currentBreakoutRoom && !isFormDisabled() ? (
                      <InviteUsersLink>
                        <EmailIcon
                          style={{
                            color: theme.colors.primary.red,
                            marginRight: 8,
                          }}
                        />
                        <SdmTypography
                          variant={'h4'}
                          margin={0}
                          style={{
                            color: theme.colors.primary.red,
                            cursor: 'pointer',
                          }}
                          onClick={handleInvite}
                        >
                          {isXsDown ? i18n.inviteXsDown : i18n.invite}
                        </SdmTypography>
                      </InviteUsersLink>
                    ) : null}
                  </div>
                </Grid>
              </Grid>
            </Grid>
            {/* //TODO: Here we must add the subtitle regarding the multibrand Name */}
            <SdmSpacer height={10} />
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12}>
                  <Controller
                    name="topic"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={(props) => (
                      <CustomFormTextField
                        onChange={(e) => {
                          props.onChange(e.target.value);
                        }}
                        value={props.value}
                        id="topic-required"
                        label={i18n.fields.topic.label}
                        fullWidth
                        disabled={isFormDisabled()}
                      />
                    )}
                  />
                  {renderError('topic')}
                </Grid>
                <SdmSpacer height={isXsDown ? 20 : 50} />
                <Grid item sm={6} xs={12}>
                  <Controller
                    name="sessionType"
                    control={control}
                    defaultValue={`${
                      !canCreateLiveChat ? 'breakout_session' : ''
                    }`}
                    rules={{ required: true }}
                    render={(props) => {
                      if (!canCreateLiveChat) return true;
                      return (
                        <SdmSelect
                          options={brOptionTypes}
                          value={props.value}
                          labelKey={'label'}
                          label={i18n.fields.sessionType.label}
                          disabledLabel={i18n.fields.sessionType.disabledLabel}
                          onSelect={(e) => {
                            setSelectedBrType(e);
                            props.onChange(e);
                          }}
                          ref={sessionSelectRef}
                          disabled={isFormDisabled()}
                        />
                      );
                    }}
                  />
                  {renderError('sessionType')}
                </Grid>
                <SdmSpacer height={30} />
                <Grid container alignItems={'center'} justify={'space-between'}>
                  {!isChatType && !isFormDisabled() ? (
                    <SdmTypography
                      variant={'h5'}
                      textAlign={'center'}
                      style={{ marginBottom: 20, width: '100%' }}
                    >
                      {i18n.fields.customError}
                    </SdmTypography>
                  ) : null}
                  {selectedBrType ? (
                    <>
                      {!isChatType ? (
                        <Grid item sm={4} xs={12}>
                          <Controller
                            name="date"
                            control={control}
                            defaultValue={
                              new Date(addDays(new Date(), delayForCreation))
                            }
                            rules={{ required: true }}
                            render={(props) => (
                              <MuiPickersUtilsProvider
                                utils={DateFnsUtils}
                                // locale={localeMap[locale]}
                              >
                                <ThemeProvider theme={defaultMaterialTheme}>
                                  <KeyboardDatePicker
                                    // variant="inline"
                                    format={'dd/MM/yyy'}
                                    autoOk
                                    value={
                                      props.value ||
                                      new Date(
                                        addDays(new Date(), delayForCreation)
                                      )
                                    }
                                    onChange={(e) => props.onChange(e)}
                                    TextFieldComponent={CustomFormTextField}
                                    disabled={isFormDisabled()}
                                    id="date-required"
                                    minDate={new Date(getMinDate())}
                                    shouldDisableDate={disableDates}
                                    label={i18n.fields.date.label}
                                    disableToolbar
                                  />
                                </ThemeProvider>
                              </MuiPickersUtilsProvider>
                            )}
                          />
                          {renderError('date')}
                        </Grid>
                      ) : null}
                      {isXsDown && <SdmSpacer height={30} />}
                      <Grid item sm={!isChatType ? 3 : 5} xs={12}>
                        <Controller
                          name="start"
                          control={control}
                          defaultValue={new Date()}
                          rules={{ required: true }}
                          render={(props) => (
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                              <ThemeProvider theme={defaultMaterialTheme}>
                                {!isChatType ? (
                                  <TimePicker
                                    variant="inline"
                                    autoOk
                                    value={props.value || new Date()}
                                    onChange={(e) => {
                                      setCustomError(false);
                                      return props.onChange(e);
                                    }}
                                    id="start-required"
                                    label={i18n.fields.start.label}
                                    disabled={isFormDisabled()}
                                    ampm={false}
                                    TextFieldComponent={CustomFormTextField}
                                  />
                                ) : (
                                  <KeyboardDateTimePicker
                                    variant="inline"
                                    ampm={false}
                                    autoOk
                                    value={props.value || new Date()}
                                    label={i18n.fields.start.label}
                                    onChange={(e) => {
                                      setCustomError(false);
                                      return props.onChange(e);
                                    }}
                                    id="start-required"
                                    disablePast={
                                      !currentBreakoutRoom && !isChatType
                                    }
                                    disabled={isFormDisabled()}
                                    TextFieldComponent={CustomFormTextField}
                                    format="yyyy/MM/dd HH:mm"
                                  />
                                )}
                              </ThemeProvider>
                            </MuiPickersUtilsProvider>
                          )}
                        />
                        {renderError(
                          'start',
                          !isChatType ? null : 'startDateTime'
                        )}
                      </Grid>
                      {isXsDown && <SdmSpacer height={30} />}
                      <Grid item sm={!isChatType ? 3 : 5} xs={12}>
                        <Controller
                          name="end"
                          control={control}
                          defaultValue={new Date()}
                          rules={{ required: true }}
                          render={(props) => (
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                              <ThemeProvider theme={defaultMaterialTheme}>
                                {!isChatType ? (
                                  <TimePicker
                                    variant="inline"
                                    value={props.value || new Date()}
                                    onChange={(e) => {
                                      setCustomError(false);
                                      return props.onChange(e);
                                    }}
                                    autoOk
                                    id="end-required"
                                    label={i18n.fields.end.label}
                                    disabled={isFormDisabled()}
                                    ampm={false}
                                    TextFieldComponent={CustomFormTextField}
                                    defaultValue={new Date()}
                                  />
                                ) : (
                                  <KeyboardDateTimePicker
                                    variant="inline"
                                    ampm={false}
                                    autoOk
                                    value={props.value || new Date()}
                                    label={i18n.fields.end.label}
                                    onChange={(e) => {
                                      setCustomError(false);
                                      return props.onChange(e);
                                    }}
                                    id="end-required"
                                    disablePast={
                                      !currentBreakoutRoom && !isChatType
                                    }
                                    disabled={isFormDisabled()}
                                    TextFieldComponent={CustomFormTextField}
                                    format="yyyy/MM/dd HH:mm"
                                  />
                                )}
                              </ThemeProvider>
                            </MuiPickersUtilsProvider>
                          )}
                        />
                        {renderError('end', !isChatType ? null : 'endDateTime')}
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
            <SdmSpacer height={isXsDown ? 20 : 50} />
            <Grid item xs={12}>
              <SdmTypography variant={'h4Regular'}>
                {i18n.fields.coverImage.title}
              </SdmTypography>
              <SdmTypography
                variant={'h4Regular'}
                style={{ color: theme.colors.greyScale.grey6 }}
              >
                {i18n.fields.coverImage.subtitle}
              </SdmTypography>
              <Controller
                name="coverImage"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={(props) => {
                  const url = getValues('coverImage');
                  return (
                    <SdmImageUploader
                      onUploaded={(value) => {
                        props.onChange(value);
                      }}
                      value={
                        url
                          ? typeof url === 'string'
                            ? `${CDN_URL}/${url}`
                            : url.data
                          : null
                      }
                      disabled={isFormDisabled()}
                    />
                  );
                }}
              />
              {renderError('coverImage')}
            </Grid>
            <SdmSpacer height={isXsDown ? 20 : 50} />
            <Grid item xs={12}>
              <SdmTypography variant={'h4Regular'}>
                {i18n.fields.description.label}
              </SdmTypography>
              <Controller
                name="description"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={(props) => (
                  <SdmRichText
                    onChange={(value) => {
                      if (value?.blocks?.[0].text.trim().length !== 0) {
                        props.onChange(value);
                      } else {
                        props.onChange(null);
                      }
                    }}
                    id="description-required"
                    value={dataHelper.parseRichText(getValues('description'))}
                    variant={'grey'}
                    formControlStyle={{
                      margin: 0,
                    }}
                    readOnly={isFormDisabled()}
                  />
                )}
              />
              {renderError('description')}
            </Grid>
            {isChatType && (
              <>
                <SdmSpacer height={isXsDown ? 20 : 50} />
                <Grid item xs={12}>
                  {fields.map((item, index) => {
                    return (
                      <div key={item.id}>
                        <SpeakerBox
                          speaker={item}
                          idx={index}
                          getValues={getValues}
                          control={control}
                          errors={errors}
                          handleRemove={handleRemove}
                          i18n={i18n.speakers}
                        />
                        <SdmSpacer height={30} />
                      </div>
                    );
                  })}
                  <SdmTypography
                    variant={'h3'}
                    margin={0}
                    style={{
                      color: theme.colors.primary.red,
                      cursor: 'pointer',
                    }}
                    textAlign={'center'}
                    onClick={() => {
                      append({
                        givenName: '',
                        familyName: '',
                        coverImage: '',
                        oldId: '',
                      });
                    }}
                  >
                    {i18n.addSpeaker}
                  </SdmTypography>
                </Grid>
              </>
            )}
            <SdmSpacer height={isXsDown ? 20 : 50} />
            <Grid item xs={12}>
              <Grid
                container
                style={{ flexDirection: isXsDown ? 'column-reverse' : 'row' }}
              >
                <Grid item sm={5} xs={12}>
                  {currentBreakoutRoom ? (
                    <>
                      {isXsDown && <SdmSpacer height={30} />}
                      <DeleteLink>
                        <DeleteIcon
                          style={{
                            color: theme.colors.primary.red,
                            marginRight: 8,
                          }}
                        />
                        <SdmTypography
                          variant={'h3'}
                          margin={0}
                          style={{
                            color: theme.colors.primary.red,
                            cursor: 'pointer',
                          }}
                          onClick={handleDeleteBr}
                        >
                          {i18n.deleteBr}
                        </SdmTypography>
                      </DeleteLink>
                    </>
                  ) : null}
                </Grid>
                <Grid item sm={7} xs={12}>
                  {customError ? (
                    <SdmTypography
                      style={{ color: `${theme.colors.primary.red}` }}
                      variant={'textBold'}
                    >
                      {i18n.fields.customError}
                    </SdmTypography>
                  ) : null}
                  <ButtonsContainer isformdisabled={isFormDisabled() ? 1 : 0}>
                    <SdmTextButton
                      variant={'red'}
                      text={i18n.back}
                      textVariant={'h4Regular'}
                      onClick={handleBack}
                      style={{
                        minWidth: isXsDown ? 120 : isSmDown ? 150 : 192,
                        height: 46,
                      }}
                    />
                    {!isFormDisabled() ? (
                      <Tooltip
                        title={
                          currentBreakoutRoom?.id
                            ? checkUpdateIsDisabled()
                              ? i18n.updateSaveTooltipDisabled
                              : i18n.updateSaveTooltip
                            : i18n.createSaveTooltip
                        }
                      >
                        <span>
                          <SdmTextButton
                            variant={'redFill'}
                            text={i18n.save}
                            textVariant={'h4Regular'}
                            type="submit"
                            disabled={customError || checkUpdateIsDisabled()}
                            style={{
                              minWidth: isXsDown ? 120 : isSmDown ? 150 : 192,
                              height: 46,
                            }}
                          />
                        </span>
                      </Tooltip>
                    ) : null}
                  </ButtonsContainer>
                </Grid>
              </Grid>
            </Grid>
            <SdmSpacer height={30} />
          </form>
          {/* //TODO: Here we must add the language select section */}
        </div>
      </Grid>
    );
  };

  const renderList = () => {
    const isChatType =
      currentBreakoutRoom?.type === breakoutRoomModel.enums.types.CHAT;

    return currentBreakoutRoom ? (
      <Grid
        item
        xs={12}
        style={{
          padding: '0 20px',
          backgroundColor: '#f3f3f3',
        }}
      >
        <div
          style={{
            margin: '50px auto 0',
            maxWidth: 736,
          }}
        >
          <InvitationList
            i18n={i18n.invitationList}
            items={invitations}
            onBan={handleBan}
            onShowNote={handleShowNote}
            onDelete={handleDelete}
            onPromote={isChatType ? handlePromote : null}
            onRoleEdit={isChatType ? handleRoleCreation : null}
            isOnlyViewMode={isFormDisabled()}
            isStarted={isStarted()}
          />
        </div>
      </Grid>
    ) : null;
  };

  return (
    <div>
      {renderDeleteDialog()}
      {renderNoteDialog()}
      <Grid container>
        {renderForm()}
        {renderList()}
      </Grid>
    </div>
  );
};

export default BreakoutRoomForm;
